/**
 *
 * AddTransferModal
 *
 */
import React, { memo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  Button,
  Cross,
  Text,
  Conditional,
  InputField,
  Space,
  RadioButton,
} from '@cashfree-intl/coherent';
import FormikComponent from 'components/Formik';
import useClickOutside from 'utils/hooks';
import { CURRENCY_SYMBOL } from 'utils/constants';
import { getItemFromLocalStorage } from 'utils/common';
import {
  StyledAddTags,
  StyledChargesSpace,
  StyledInputWithAction,
  StyledMerchantTransferSpace,
  StyledODSForm,
  StyledOptionalTag,
  StyledTagsInput,
  StyledVendorIdInput,
  StyledVendorTransferSpace,
} from './styled';
export function OnDemandSettlementForm({
  onClose,
  getTransferBalance,
  getTransferCharges,
  handleBalanceTransfer,
}) {
  const modalRef = useRef(null);
  useClickOutside(modalRef, onClose);
  const [tags, setTags] = useState([{ tagName1: '', tagValue1: '' }]);
  const [balances, setBalances] = useState({});
  const [charges, setCharges] = useState({});
  const merchantId = getItemFromLocalStorage('merchantId');
  const initialValues = {
    vendorId: '',
    transferType: 'ON_DEMAND',
    transferFrom: '',
    amount: '',
  };
  const handleFormSubmit = (values, actions) => {
    const payload = { ...values };
    const { vendorId, transferType, transferFrom, amount } = payload;
    const transferAmount = parseFloat(amount, 2);
    let tagsObj = {};
    Object.keys(payload).forEach(key => {
      if (key.includes('tagName')) {
        const splitArr = key.split('tagName');
        if (splitArr.length > 1) {
          const count = splitArr[1];
          if (`tagValue${count}`) {
            let tagVal = payload[`tagValue${count}`];
            // eslint-disable-next-line no-restricted-globals
            if (!isNaN(tagVal)) {
              tagVal = +tagVal;
            }
            tagsObj = {
              ...tagsObj,
              [payload[key]]: tagVal,
            };
          }
        }
      }
    });
    let updatedPayload = {
      vendorId,
      transferType,
      transferFrom,
      transferAmount,
    };
    if (Object.keys(tagsObj).length > 0) {
      updatedPayload = { ...updatedPayload, tags: { ...tagsObj } };
    }
    handleBalanceTransfer({
      payload: updatedPayload,
      actions,
      merchantId,
    });
  };
  return (
    <Modal className="common-modal-wrapper" ref={modalRef} $maxWidth={536} open>
      <ModalHeader>
        Create On Demand Settlement to Vendor
        <Cross
          onClick={onClose}
          data-event-name="Form_onDemandSettlement_Icon_Cancel"
          data-ignore-inner-text="true"
        />
      </ModalHeader>
      <StyledODSForm>
        <div className="modal-content">
          <FormikComponent
            initialValues={initialValues}
            onSubmit={handleFormSubmit}
          >
            {({
              values,
              handleFieldBlur,
              handleFieldChange,
              handleSubmit,
              errors,
              touched,
              status,
              setFieldValue,
              setStatus,
              setFieldTouched,
              ...formikProps
            }) => {
              const isLoading =
                !formikProps.isValidating && formikProps.isSubmitting;
              const disableField =
                !Object.keys(balances).length || !Object.keys(charges).length;
              const handleAddMoreTagsClick = () => {
                const tagName = `tagName${tags.length + 1}`;
                const tagValue = `tagValue${tags.length + 1}`;
                setTags([...tags, { [tagName]: '', [tagValue]: '' }]);
              };
              const checkInsufficientBalance = (
                response,
                amount,
                transferFrom,
              ) => {
                const transferBalance =
                  transferFrom === 'MERCHANT'
                    ? balances?.merchantUnsettled
                    : balances?.vendorUnsettled;
                const totalCharges =
                  response.serviceCharges + response.serviceTax;
                const { isPostpaid, billedTo } = response;
                if (
                  !isPostpaid &&
                  transferFrom === billedTo &&
                  totalCharges + parseFloat(parseFloat(amount).toFixed(2)) >
                    transferBalance
                ) {
                  setStatus({
                    ...status,
                    amount: `Insufficient Balance. You can withdraw a maximum of ₹${transferBalance -
                      totalCharges} post charges deduction.`,
                  });
                } else {
                  setStatus({ ...status, amount: '' });
                }
              };
              const getCharges = (
                amount = values.amount,
                transferFrom = values?.transferFrom,
              ) => {
                if (amount)
                  getTransferCharges({
                    data: { merchantId, amount },
                    cb: response => {
                      const { serviceCharges, serviceTax } = response;
                      setCharges({ serviceCharges, serviceTax });
                      checkInsufficientBalance(response, amount, transferFrom);
                    },
                    errorCb: () => {
                      setCharges({});
                    },
                  });
              };
              const handleVendorIdChange = (e, data) => {
                getTransferBalance({
                  data: { merchantId, vendorId: e.target.value },
                  cb: response => {
                    setStatus({ ...status, vendorId: '' });
                    const { merchantUnsettled, vendorUnsettled } = response;
                    // set Balances for merchantUnsettled and vendorUnsettled
                    setBalances({
                      ...balances,
                      merchantUnsettled,
                      vendorUnsettled,
                    });
                    setCharges({});
                    if (values.amount && isAmountValid(values?.amount)) {
                      getCharges();
                    }
                  },
                  errorCb: err => {
                    if (err?.message === "Split Vendor doesn't exist.") {
                      setStatus({ ...status, vendorId: 'Invalid Vendor ID' });
                    }
                    setBalances({});
                    setCharges({});
                  },
                });
                handleFieldChange(e, data);
              };
              const checkAmountError = (
                transferFrom,
                amount = values?.amount,
              ) => {
                let updatedStatus = { ...status };
                const maxAllowedAmount =
                  transferFrom === 'MERCHANT'
                    ? balances?.merchantUnsettled
                    : balances?.vendorUnsettled;
                if (amount > maxAllowedAmount) {
                  updatedStatus = {
                    ...updatedStatus,
                    amount:
                      'Entered amount is greater than the available balance. Enter a lesser amount.',
                  };
                } else {
                  updatedStatus = { ...updatedStatus, amount: '' };
                }
                setStatus(updatedStatus);
              };
              const isAmountValid = (
                amount,
                transferFrom = values?.transferFrom,
              ) => {
                if (
                  !transferFrom ||
                  !values.vendorId ||
                  !Object.keys(balances).length
                )
                  return false;
                const maxAmountAllowed =
                  transferFrom === 'MERCHANT'
                    ? balances?.merchantUnsettled
                    : balances?.vendorUnsettled;
                return amount <= maxAmountAllowed;
              };
              const handleRadioChange = transferFrom => {
                setFieldValue('transferFrom', transferFrom);
                setCharges({});
                if (values?.amount) {
                  checkAmountError(transferFrom);
                }
                if (isAmountValid(values?.amount, transferFrom)) {
                  getCharges(values?.amount, transferFrom);
                }
              };
              const handleAmountBlur = (e, data) => {
                if (e.target.value < 10) {
                  setStatus({
                    ...status,
                    amount:
                      'Transfer amount should be greater than or equal to 10',
                  });
                } else if (isAmountValid(e.target.value)) {
                  getCharges(e.target.value);
                }
                if (e.target.value >= 10) {
                  checkAmountError(values?.transferFrom, e.target.value);
                }
                handleFieldBlur(e, data);
              };
              return (
                <form autoComplete="off" onSubmit={handleSubmit}>
                  <StyledVendorIdInput className="mb-3">
                    <InputField
                      label="Vendor ID"
                      placeholder="Enter Vendor Id"
                      name="vendorId"
                      value={values.vendorId}
                      onChange={(e, data) => {
                        handleVendorIdChange(e, data);
                      }}
                      errors={status}
                      touched={status?.vendorId ? { vendorId: true } : {}}
                      onBlur={handleFieldBlur}
                    />
                  </StyledVendorIdInput>
                  <Text variant="b14" color="bodyLight" className="mb-2">
                    Choose Debit Account
                  </Text>
                  <Space justifyContent="space-between">
                    <StyledMerchantTransferSpace
                      transferFrom={values.transferFrom}
                      direction="column"
                    >
                      <Space gap={1}>
                        <RadioButton
                          name="transferFrom"
                          checked={values.transferFrom === 'MERCHANT'}
                          onClick={() => handleRadioChange('MERCHANT')}
                          data-event-name="Form_onDemandSettlement_Radio_TransferForm"
                          data-event-properties={JSON.stringify({
                            value: 'MERCHANT',
                          })}
                          data-ignore-inner-text="true"
                        />
                        <Text variant="b14">Self Unsettled Balance</Text>
                      </Space>
                      <Text className="pl-3">
                        {Object.keys(balances).length > 0
                          ? balances?.merchantUnsettled
                          : '-'}
                      </Text>
                    </StyledMerchantTransferSpace>
                    <StyledVendorTransferSpace
                      direction="column"
                      transferFrom={values.transferFrom}
                    >
                      <Space gap={1}>
                        <RadioButton
                          name="transferFrom"
                          checked={values.transferFrom === 'VENDOR'}
                          onClick={() => handleRadioChange('VENDOR')}
                          data-event-name="Form_onDemandSettlement_Radio_TransferForm"
                          data-event-properties={JSON.stringify({
                            value: 'VENDOR',
                          })}
                          data-ignore-inner-text="true"
                        />
                        <Text variant="b14">
                          Vendor&apos;s Unsettled Balance
                        </Text>
                      </Space>
                      <Text className="pl-3">
                        {Object.keys(balances).length > 0
                          ? balances?.vendorUnsettled
                          : '-'}
                      </Text>
                    </StyledVendorTransferSpace>
                  </Space>
                  <Text variant="b14" color="bodyLight" className="mb-1 mt-3">
                    Amount to withdraw:
                  </Text>
                  <StyledInputWithAction
                    name="amount"
                    value={values.amount}
                    label={CURRENCY_SYMBOL.INR}
                    type="number"
                    placeholder="10,000"
                    onChange={handleFieldChange}
                    onBlur={(e, data) => handleAmountBlur(e, data)}
                    disabled={
                      Object.keys(balances).length === 0 || !values.transferFrom
                    }
                    errors={status}
                    touched={status?.amount ? { amount: true } : {}}
                    fluid
                  />
                  <Conditional
                    if={Object.keys(charges).length > 0 && !status?.amount}
                  >
                    <StyledChargesSpace
                      direction="column"
                      gap={1}
                      className="pt-1 px-2 pb-3 mt-2"
                    >
                      <Text variant="b14" strong>
                        Applicable Charges :
                      </Text>
                      <Space justifyContent="space-between" className="ml-1">
                        <Text variant="b12" color="bodyLight">
                          Service Charge
                        </Text>
                        <Text variant="b12">
                          (-) {CURRENCY_SYMBOL.INR}{' '}
                          {parseFloat(charges?.serviceCharges).toFixed(2)}
                        </Text>
                      </Space>
                      <Space justifyContent="space-between" className="ml-1">
                        <Text variant="b12" color="bodyLight">
                          18% GST on Service Charge
                        </Text>
                        <Text variant="b12">
                          (-) {CURRENCY_SYMBOL.INR}{' '}
                          {parseFloat(charges?.serviceTax).toFixed(2)}
                        </Text>
                      </Space>
                    </StyledChargesSpace>
                  </Conditional>
                  <Space gap={1} className="mb-2 mt-3">
                    <Text variant="b14" color="bodyLight">
                      Add Tags
                    </Text>
                    <StyledOptionalTag variant="b12" color="bodyLight">
                      Optional
                    </StyledOptionalTag>
                  </Space>
                  {tags.map((tag, i) => (
                    <Space justifyContent="space-between" key={tag}>
                      <StyledTagsInput
                        name={`tagName${i + 1}`}
                        value={values[`tagName${i + 1}`]}
                        placeholder="For ex: Order Id"
                        label="Tag Name"
                        onChange={handleFieldChange}
                      />
                      <StyledTagsInput
                        name={`tagValue${i + 1}`}
                        value={values[`tagValue${i + 1}`]}
                        placeholder="For ex: 12345666"
                        label="Tag Value"
                        onChange={handleFieldChange}
                      />
                    </Space>
                  ))}
                  <StyledAddTags
                    color="primary"
                    variant="b14"
                    onClick={handleAddMoreTagsClick}
                    data-event-name="Form_onDemandSettlement_Icon_AddMoreTags"
                    data-ignore-inner-text="true"
                  >
                    +Add More Tags
                  </StyledAddTags>
                  <div className="cta-wrapper">
                    <Button
                      type="button"
                      link
                      className="link"
                      onClick={onClose}
                      data-event-name="Form_onDemandSettlement_Button_Cancel"
                    >
                      Cancel
                    </Button>
                    <Button
                      primary
                      type="submit"
                      disabled={disableField}
                      loading={isLoading}
                      data-event-name="Primary_Button_TransferBalance"
                    >
                      Transfer Balance
                    </Button>
                  </div>
                </form>
              );
            }}
          </FormikComponent>
        </div>
      </StyledODSForm>
    </Modal>
  );
}
OnDemandSettlementForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  getTransferBalance: PropTypes.func.isRequired,
  getTransferCharges: PropTypes.func.isRequired,
  handleBalanceTransfer: PropTypes.func.isRequired,
};
export default memo(OnDemandSettlementForm);
